const PERSIAN_DIGITS = "۰۱۲۳۴۵۶۷۸۹";

export function toPersianDigits(str) {
  return String(str ?? "").replace(/\d/g, (d) => PERSIAN_DIGITS[parseInt(d, 10)]);
}

function persianDigitsToLatin(str) {
  return String(str ?? "").replace(/[۰-۹]/g, (ch) => {
    const index = PERSIAN_DIGITS.indexOf(ch);
    return index >= 0 ? String(index) : ch;
  });
}

export function getPersianYear(dateInput) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return null;

  const parts = new Intl.DateTimeFormat("fa-IR-u-ca-persian", { year: "numeric" }).formatToParts(date);
  const raw = parts.find((part) => part.type === "year")?.value ?? "";
  const year = parseInt(persianDigitsToLatin(raw), 10);
  return Number.isNaN(year) ? null : year;
}

/**
 * تاریخ شمسی برای نمایش:
 * - امسال: بدون سال
 * - پارسال: سال دو رقمی
 * - قدیمی‌تر: سال کامل
 */
export function formatPersianDateSmart(dateInput, now = new Date()) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  const dateYear = getPersianYear(date);
  const currentYear = getPersianYear(now);
  if (dateYear == null || currentYear == null) return "";

  const baseOpts = { calendar: "persian", month: "2-digit", day: "2-digit" };

  if (dateYear === currentYear) {
    return toPersianDigits(date.toLocaleDateString("fa-IR-u-ca-persian", baseOpts));
  }

  if (dateYear === currentYear - 1) {
    return toPersianDigits(
      date.toLocaleDateString("fa-IR-u-ca-persian", { ...baseOpts, year: "2-digit" })
    );
  }

  return toPersianDigits(
    date.toLocaleDateString("fa-IR-u-ca-persian", { ...baseOpts, year: "numeric" })
  );
}

/**
 * تاریخ مثل تلگرام: امسال «۲۱ مهر»، سال‌های دیگر «۲۱ مهر ۱۴۰۳».
 * short=true (ستون زمان لیست): سال‌های دیگر «۱۴۰۳/۰۷/۲۱» تا ستون باریک بماند.
 */
export function formatPersianDayMonth(dateInput, now = new Date(), { short = false } = {}) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";
  const sameYear = getPersianYear(date) === getPersianYear(now);
  if (sameYear) {
    return toPersianDigits(date.toLocaleDateString("fa-IR-u-ca-persian", { day: "numeric", month: "long" }));
  }
  if (short) {
    return toPersianDigits(
      date.toLocaleDateString("fa-IR-u-ca-persian", { year: "numeric", month: "2-digit", day: "2-digit" })
    );
  }
  return toPersianDigits(
    date.toLocaleDateString("fa-IR-u-ca-persian", { day: "numeric", month: "long", year: "numeric" })
  );
}

function startOfLocalDay(dateInput) {
  const date = dateInput instanceof Date ? new Date(dateInput) : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return null;
  date.setHours(0, 0, 0, 0);
  return date;
}

function sameLocalCalendarDay(a, b) {
  if (!a || !b) return false;
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

const PERSIAN_WEEKDAYS = [
  "یکشنبه",
  "دوشنبه",
  "سه‌شنبه",
  "چهارشنبه",
  "پنج‌شنبه",
  "جمعه",
  "شنبه",
];

/**
 * زمان نمایشی لیست گفت‌وگوها: امروز → ساعت، دیروز → «دیروز»، هفتهٔ اخیر → نام روز، قدیمی‌تر → تاریخ.
 */
export function formatChatListTime(dateInput, now = new Date()) {
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  const dateDay = startOfLocalDay(date);
  const today = startOfLocalDay(now);
  if (!dateDay || !today) return "";

  const pad = (n) => String(n).padStart(2, "0");
  if (sameLocalCalendarDay(dateDay, today)) {
    return toPersianDigits(`${pad(date.getHours())}:${pad(date.getMinutes())}`);
  }

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameLocalCalendarDay(dateDay, yesterday)) {
    return "دیروز";
  }

  const dayDiff = Math.round((today - dateDay) / (24 * 60 * 60 * 1000));
  if (dayDiff > 1 && dayDiff < 7) {
    return PERSIAN_WEEKDAYS[date.getDay()] || "";
  }

  return formatPersianDayMonth(date, now, { short: true });
}

/**
 * زمان نمایشی پیام / نوار پخش صوتی:
 * - امروز: «امروز · ساعت»
 * - دیروز: «دیروز · ساعت»
 * - قدیمی‌تر: «ساعت · تاریخ»
 */
export function formatMessageDisplayTime(dateInput, now = new Date()) {
  if (!dateInput) return "";
  const date = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (Number.isNaN(date.getTime())) return "";

  const timePart = toPersianDigits(
    date.toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit" })
  );

  const dateDay = startOfLocalDay(date);
  const today = startOfLocalDay(now);
  if (!dateDay || !today) return timePart;

  if (sameLocalCalendarDay(dateDay, today)) {
    return `امروز · ${timePart}`;
  }

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (sameLocalCalendarDay(dateDay, yesterday)) {
    return `دیروز · ${timePart}`;
  }

  const datePart = formatPersianDateSmart(date, now);
  return datePart ? `${timePart} · ${datePart}` : timePart;
}
